import React, { useState } from 'react'
import IconButton from '@material-ui/core/IconButton'
import SearchIcon from '@material-ui/icons/Search'
import { TextField, makeStyles } from '@material-ui/core'
import SendIcon from '@material-ui/icons/Send'
import toast from 'react-hot-toast'
import { useTranslation } from '@flint/locales'
import GeoJSON from 'ol/format/GeoJSON'
import { RasedMap } from 'pages/Map/RasedMap'
import { gqlClient } from 'lib/graphql'
import { SEARCH_QA_RECORDS } from 'global/graphql/SimpleProjectQA'
import { getSearchFieldOptions, logError } from 'utils'
import { getPolygonExtent } from 'utils/map.utils'

const useStyle = makeStyles(() => ({
  root: {
    margin: '2px 10px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconBtn: {
    background: 'white',
    padding: '.2rem',
    borderRadius: '10px',
    '&:hover': { background: 'white' },
  },
  searchInput: {
    backgroundColor: 'white',
    margin: '0px 6px',
    width: '160px',
  },
}))

interface Props {
  mapRef: React.MutableRefObject<RasedMap | null>
  order: any
  onRecordFound: (record: any) => void
}

export const QARecordSearch = ({ mapRef, order, onRecordFound }: Props) => {
  const classes = useStyle()
  const { t } = useTranslation()
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const parsedSchema =
    typeof order?.jsonSchema === 'string'
      ? JSON.parse(order.jsonSchema)
      : order?.jsonSchema
  const configuration = parsedSchema?.rased?.configuration
  const searchFieldOptions = getSearchFieldOptions(configuration)

  const handleSearch = async () => {
    if (!searchValue) {
      toast.error(t('please-enter-search-value'), { duration: 2000 })
      return
    }
    if (!searchFieldOptions.length) {
      toast.error(t('no-search-fields-configured'), { duration: 2000 })
      return
    }

    try {
      const searchField = searchFieldOptions[0]
      const result = await gqlClient.query({
        query: SEARCH_QA_RECORDS,
        variables: {
          orderId: order.id,
          filters: [
            { field: searchField.value, value: searchValue, clause: 'iexact' },
          ],
          pageInfo: { limit: 1, offset: 0 },
        },
        fetchPolicy: 'network-only',
      })

      const record = result?.data?.qaRecords?.data?.[0]

      if (!record) {
        toast.error(t('no-record-found'), { duration: 2000 })
        return
      }

      if (mapRef?.current && record.geometry?.geometries?.[0]) {
        try {
          const originalFeature = {
            type: 'Feature' as const,
            id: `${order.dataLayer?.wmsFeatureTypeName}.${record.id}`,
            geometry: record.geometry,
            geometry_name: 'geometry',
            properties: {
              id: parseInt(record.id, 10),
              map_data: JSON.stringify(record.mapData || {}),
            },
          }

          const extent = getPolygonExtent(originalFeature)
          mapRef.current.getView().fit(extent, {
            duration: 2000,
            padding: [150, 150, 150, 150],
            maxZoom: 18,
          })

          const olFeature = new GeoJSON().readFeature(originalFeature, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857',
          })
          const transformedFeature = new GeoJSON().writeFeatureObject(olFeature)

          mapRef.current.clearHighlightedFeature()
          mapRef.current.highlightFeature(transformedFeature as any)
        } catch (geometryError) {
          logError('MapError', geometryError, [
            'qa record search',
            'geometry processing',
          ])
          toast.error(t('error-processing-geometry'), { duration: 2000 })
        }
      }

      onRecordFound(record)
      setIsSearchOpen(false)
      setSearchValue('')
    } catch (error) {
      logError('NetworkError', error, ['qa', 'searching record'])
    }
  }

  return (
    <div className={classes.root}>
      <IconButton
        className={classes.iconBtn}
        onClick={() => setIsSearchOpen(!isSearchOpen)}
      >
        <SearchIcon color="primary" />
      </IconButton>

      {isSearchOpen && (
        <>
          <TextField
            className={classes.searchInput}
            id="search-qa-record"
            variant="outlined"
            placeholder={t('search-for-record')}
            size="small"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <IconButton className={classes.iconBtn} onClick={handleSearch}>
            <SendIcon color="primary" />
          </IconButton>
        </>
      )}
    </div>
  )
}
